// Request vetting for a contractor who is not on the register — RON-5466 Phase 4.
//
// NOT A BOOKING, and the screen says so twice. Vetting is a separate approval that has to complete
// before the contractor can be invited, so this sends a request to the compliance admin team and
// returns the host to the list. Letting it create a visit as well would put an unvetted contractor
// on a site.
//
// TWO CARDS, matching the prototype's `CReqForm`: who they are, then why they need to be on site.
// The split is not decoration — the second card is the only part a compliance admin actually reads
// to make the decision, and burying it under four identity fields made it look like an afterthought.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import CustomDatePicker from "../../Common/CustomDatePicker.jsx";
import { EMAIL_PATTERN } from "../../../utils/CommonUtils.js";
import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";
import { fromISODate, toISODate } from "../visitorDateUtils.js";

const ContractorRequestVetting = ({
  initialEmail = "",
  // The visit date, when the host already has one — the step-2 re-vetting route does. It is what
  // makes the server classify this as a "clearance ends before the visit" request (§4.10).
  initialNeededBy = "",
  isSubmitting,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation("common");

  // Carried over from the lookup so the host does not retype the address they just searched.
  const [form, setForm] = useState({
    email: initialEmail,
    name: "",
    company: "",
    trade: "",
    reason: "",
    neededBy: initialNeededBy,
  });
  const [errors, setErrors] = useState({});

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors({});
  };

  const submit = () => {
    const found = {};

    if (!form.email.trim()) {
      found.email = "react_visitors_error_email_required";
    } else if (!EMAIL_PATTERN.test(form.email.trim())) {
      // Same pattern the rest of the app validates against.
      found.email = "react_visitors_error_email_invalid";
    }
    if (!form.company.trim()) found.company = "react_visitors_error_company";
    // REQUIRED, matching the prototype's `canSubmit`. A request with no reason gives the compliance
    // admin nothing to decide on, so they come back asking — which costs the host a day and makes
    // the form look like it worked when it did not.
    if (!form.reason.trim()) found.reason = "react_visitors_error_reason";

    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }

    onSubmit({
      email: form.email.trim(),
      // OPTIONAL, and sent empty rather than falling back to the company name. The agreed payload
      // (RON-6002 Q3) carries `name` and `company` separately, so a company name in the contact
      // field would read to the admin as a person who does not exist.
      name: form.name.trim(),
      company: form.company.trim(),
      trade: form.trade.trim(),
      reason: form.reason.trim(),
      neededBy: form.neededBy,
    });
  };

  const field = (key, labelKey, { required = false, type = "text", placeholderKey } = {}) => {
    const label = t(labelKey);

    return (
      <VisitorFormRow
        id={`vetting-${key}`}
        label={label}
        required={required}
        error={errors[key] ? t(errors[key]) : null}
      >
        <input
          id={`vetting-${key}`}
          type={type}
          placeholder={placeholderKey ? t(placeholderKey) : `${label}${required ? "*" : ""}`}
          className={fieldClassName(form[key], errors[key])}
          value={form[key]}
          onChange={(event) => setField(key, event.target.value)}
        />
      </VisitorFormRow>
    );
  };

  return (
    <div className="visitor-vetting-request">
      {/* A plain intro under the shell's heading, not a card of its own. It WAS a card titled
          "Request vetting" — directly beneath the shell heading reading "Request vetting", which
          said the same words twice and cost a card's worth of height to do it. The prototype has
          an h1 and a paragraph; this is the paragraph. */}
      <p className="visitor__subtitle">{t("react_visitors_request_vetting_hint")}</p>

      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_contractor_found")}</p>

        {/* Company leads, because it is the only identity field the request cannot do without —
            a trade and a contact name are useful, a company is what gets vetted. */}
        {field("company", "company", {
          required: true,
          placeholderKey: "react_visitors_vetting_company_placeholder",
        })}

        <div className="visitor-field-grid">
          {field("name", "react_visitors_field_contact_name")}
          {field("trade", "react_visitors_field_trade", {
            placeholderKey: "react_visitors_vetting_trade_placeholder",
          })}
        </div>

        {field("email", "email_address", { required: true, type: "email" })}
      </div>

      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_vetting_why_title")}</p>

        {/* Free text, because the compliance admin team decide on the case rather than on a
            category. */}
        <VisitorFormRow
          id="vetting-reason"
          label={t("react_visitors_field_reason")}
          required
          error={errors.reason ? t(errors.reason) : null}
        >
          <textarea
            id="vetting-reason"
            rows={3}
            placeholder={t("react_visitors_vetting_reason_placeholder")}
            className={fieldClassName(form.reason, errors.reason, false)}
            value={form.reason}
            onChange={(event) => setField("reason", event.target.value)}
          />
        </VisitorFormRow>

        {/* Parity map RQ7. Optional, and the one field that tells the compliance team whether this
            is urgent — a request for next Tuesday and one for "sometime" need different queues. */}
        <VisitorFormRow
          id="vetting-needed-by"
          label={t("react_visitors_field_needed_when")}
        >
          <CustomDatePicker
            id="vetting-needed-by"
            selected={fromISODate(form.neededBy)}
            minDate={new Date()}
            onChange={(date) => setField("neededBy", toISODate(date))}
            // OPENS UPWARDS. This is the last field on the form, so a calendar dropped below it
            // falls off the bottom of the card and the viewport — the host scrolls to reach a
            // date they can only half see. react-datepicker still flips back down when there is
            // no room above, so a short viewport is no worse than before.
            popperPlacement="top-start"
            // The browser's own suggestion list sat ON TOP of the calendar, offering previously
            // typed dates as plain text over the grid. Nothing is gained by it here — the field
            // is a picker, and its own dropdown is the way to answer it.
            autoComplete="off"
          />
        </VisitorFormRow>
      </div>

      <div className="visitor-wizard__actions">
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("react_visitors_back")}
        </button>
        {/* Validates on click rather than sitting disabled until valid, which is what the prototype
            does. A disabled button never says WHY it is disabled; the inline errors do, and the
            rest of the wizard already works this way. */}
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={submit}
          disabled={isSubmitting}
        >
          {t("react_visitors_send_request")}
        </button>
      </div>
    </div>
  );
};

export default ContractorRequestVetting;
